/**
 * Typed path builders. URLs are constructed HERE and nowhere else.
 *
 * That discipline is what keeps an open question cheap: workspace slugs are assumed
 * globally unique, so the org is implicit. If that turns out to be false, every URL gains
 * an `/o/:orgSlug` segment — and this is the only file that changes.
 */
function previewPath(clientId: string, section: string, returnTo?: string): string {
  const base = `/client-preview/${encodeURIComponent(clientId)}/${section}`
  return returnTo ? `${base}?returnTo=${encodeURIComponent(returnTo)}` : base
}

export const paths = {
  login: () => '/login',
  noAccess: () => '/no-access',
  /** Browser-extension handoff. Auth survives because RequireAuth preserves this full URL. */
  extensionGmail: () => '/extension/gmail',
  extensionConnect: () => '/extension/connect',

  workspace: (ws: string) => `/w/${ws}`,
  /** Team pulse: leaderboards, change vs the last period, your standing. */
  overview: (ws: string) => `/w/${ws}/overview`,
  /** The one dashboard: the workspace's board, filtered to a person (the caller by default). */
  work: (ws: string) => `/w/${ws}/work`,
  /** A work item opens as a drawer over the board, so the URL is the board plus ?card=. */
  workItem: (ws: string, cardId: string) => `/w/${ws}/work?card=${cardId}`,
  /** Everything waiting on the caller's sign-off, across every board. */
  /** Open work past its due date, and whether anyone has said when it will land. */
  overdue: (ws: string) => `/w/${ws}/overdue`,
  approvals: (ws: string) => `/w/${ws}/approvals`,
  /** Where the caller has been tagged and not yet answered. */
  comms: (ws: string) => `/w/${ws}/comms`,
  /** Who is working on what today, and the walkable office floor. */
  teamToday: (ws: string) => `/w/${ws}/today`,
  /** Notes, documents, sheets, diagrams and recordings; private or shared. */
  scribble: (ws: string) => `/w/${ws}/scribble`,
  scribbleItem: (ws: string, id: string) => `/w/${ws}/scribble/${id}`,
  /** Everyone's daily diary, by month. */
  diary: (ws: string) => `/w/${ws}/diary`,
  office: (ws: string) => `/w/${ws}/office`,
  drafts: (ws: string) => `/w/${ws}/drafts`,
  meetings: (ws: string) => `/w/${ws}/meetings`,
  /** Asks from clients that nobody has accepted or refused yet. */
  requests: (ws: string) => `/w/${ws}/requests`,
  /** The Support tab: one clock per customer issue, from first message to closed. */
  supportDesk: (ws: string) => `/w/${ws}/support`,
  supportDeskCase: (ws: string, caseId: string) => `/w/${ws}/support?case=${encodeURIComponent(caseId)}`,
  /** One support ticket on its own page (the full view of the slide-in). */
  supportDeskTicket: (ws: string, caseId: string) => `/w/${ws}/support/ticket/${encodeURIComponent(caseId)}`,
  supportDeskDashboard: (ws: string) => `/w/${ws}/support/dashboard`,
  /** Support Dashboard: the Intercom/L1/L2/L3 lifecycle and SLA control surface. */
  supportDashboard: (ws: string) => `/w/${ws}/support-tickets`,
  supportDashboardTicket: (ws: string, ticketId: string) => `/w/${ws}/support-tickets?ticket=${encodeURIComponent(ticketId)}`,
  /** L3 delivery queue, intentionally separate from the Support Dashboard. */
  supportTicketQueue: (ws: string) => `/w/${ws}/support-ticket`,
  supportTicketQueueItem: (ws: string, ticketId: string) => `/w/${ws}/support-ticket?ticket=${encodeURIComponent(ticketId)}`,
  /** Backwards-compatible builders for existing Support Dashboard callers/bookmarks. */
  supportTickets: (ws: string) => `/w/${ws}/support-tickets`,
  supportTicket: (ws: string, ticketId: string) => `/w/${ws}/support-tickets?ticket=${encodeURIComponent(ticketId)}`,
  /** Reports home, and one full page per report. */
  reports: (ws: string) => `/w/${ws}/reports`,
  reportsSupport: (ws: string) => `/w/${ws}/reports/support`,
  /** Daily reports: file, history, who has filed, and settings for managers. */
  reporting: (ws: string) => `/w/${ws}/reporting`,
  /** Finance requests, spend tracker and the caller's assigned financial approvals. */
  /** Sales pipeline. Only people granted the CRM see it. */
  /** Pilot engagements and their health. */
  pilots: (ws: string) => `/w/${ws}/pilots`,
  pipeline: (ws: string) => `/w/${ws}/pipeline`,
  pipelineDashboard: (ws: string) => `/w/${ws}/pipeline/dashboard`,
  pipelineCheckin: (ws: string) => `/w/${ws}/pipeline/check-in`,
  pipelineReport: (ws: string) => `/w/${ws}/pipeline/report`,
  pipelineSettings: (ws: string) => `/w/${ws}/pipeline/settings`,
  /** TapCRMv2 (staging preview): the CRM in Tapwork, off GoHighLevel. */
  crm2: (ws: string) => `/w/${ws}/crm2`,
  crm2Board: (ws: string) => `/w/${ws}/crm2/pipelines`,
  crm2Accounts: (ws: string) => `/w/${ws}/crm2/accounts`,
  crm2Account: (ws: string, id: string) => `/w/${ws}/crm2/accounts/${encodeURIComponent(id)}`,
  crm2Settings: (ws: string) => `/w/${ws}/crm2/settings`,
  crm2Dashboard: (ws: string) => `/w/${ws}/crm2/dashboard`,
  crm2Contacts: (ws: string) => `/w/${ws}/crm2/contacts`,
  crm2Contact: (ws: string, id: string) => `/w/${ws}/crm2/contacts/${encodeURIComponent(id)}`,
  crm2Conversations: (ws: string) => `/w/${ws}/crm2/conversations`,
  /** TapPM (staging preview): the projects TapCRMv2 opens at Discovery. */
  pm2: (ws: string) => `/w/${ws}/pm`,
  pm2Project: (ws: string, id: string) => `/w/${ws}/pm/projects/${encodeURIComponent(id)}`,
  pm2MyTasks: (ws: string) => `/w/${ws}/pm/my-tasks`,
  /** Presentation mode: the shell renders any `/projects/:id/present` route bare. */
  pm2ProjectPresent: (ws: string, id: string) => `/w/${ws}/pm/projects/${encodeURIComponent(id)}/present`,
  crm: (ws: string) => `/w/${ws}/crm`,
  /**
   * The CRM on GoHighLevel, in GHL's own menu order: the funnel dashboard, the team inbox,
   * contacts, accounts (GHL's companies), opportunities (the board), tasks and meetings.
   */
  crmDashboard: (ws: string) => `/w/${ws}/crm/dashboard`,
  crmConversations: (ws: string) => `/w/${ws}/crm/conversations`,
  crmContacts: (ws: string) => `/w/${ws}/crm/contacts`,
  crmContact: (ws: string, id: string) => `/w/${ws}/crm/contacts/${encodeURIComponent(id)}`,
  crmAccounts: (ws: string) => `/w/${ws}/crm/accounts`,
  crmAccount: (ws: string, key: string) => `/w/${ws}/crm/accounts/${encodeURIComponent(key)}`,
  crmBoard: (ws: string) => `/w/${ws}/crm/board`,
  crmTasks: (ws: string) => `/w/${ws}/crm/tasks`,
  crmMeetings: (ws: string) => `/w/${ws}/crm/meetings`,
  product: (ws: string) => `/w/${ws}/product`,
  procurement: (ws: string) => `/w/${ws}/procurement`,
  hr: (ws: string) => `/w/${ws}/hr`,
  finance: (ws: string) => `/w/${ws}/finance`,
  financeRequest: (ws: string, requestId: string) => `/w/${ws}/finance?request=${encodeURIComponent(requestId)}`,
  clients: (ws: string) => `/w/${ws}/clients`,
  clientDetail: (ws: string, clientId: string) => `/w/${ws}/clients?client=${encodeURIComponent(clientId)}`,
  /** The engagements: what kind of work, for which client, in which geography. */
  projects: (ws: string) => `/w/${ws}/projects`,
  /** One project as a page: overview, its own kanban, and the plan. */
  projectPage: (ws: string, projectId: string) => `/w/${ws}/projects/${encodeURIComponent(projectId)}`,
  /** A Client Project's dashboard: phases, Gantt, action registry, health. */
  clientProject: (ws: string, projectId: string) => `/w/${ws}/projects/${encodeURIComponent(projectId)}/dashboard`,
  /** The same dashboard for screen sharing: no menu, top bar, notifications or Office. */
  clientProjectPresent: (ws: string, projectId: string) => `/w/${ws}/projects/${encodeURIComponent(projectId)}/present`,
  projectDetail: (ws: string, projectId: string) => `/w/${ws}/projects?project=${encodeURIComponent(projectId)}`,
  /** Delivery metrics: SLA adherence, pick-up, cycle and lead time, estimate accuracy. */
  insights: (ws: string) => `/w/${ws}/insights`,
  archive: (ws: string) => `/w/${ws}/archive`,
  people: (ws: string) => `/w/${ws}/people`,
  /** One person's plate: what they are on, what is queued, blocked, awaiting their sign-off. */
  person: (ws: string, memberId: string) => `/w/${ws}/people/${memberId}`,

  teams: (ws: string) => `/w/${ws}/teams`,
  /** A team's week: what it is on, what it did, by delivery type. */
  team: (ws: string, teamId: string) => `/w/${ws}/teams/${teamId}`,

  settings: (ws: string) => `/w/${ws}/settings`,
  settingsMembers: (ws: string) => `/w/${ws}/settings/members`,
  settingsRoles: (ws: string) => `/w/${ws}/settings/roles`,
  settingsRole: (ws: string, roleId: string) => `/w/${ws}/settings/roles/${roleId}`,
  settingsFields: (ws: string) => `/w/${ws}/settings/fields`,
  settingsIntegrations: (ws: string) => `/w/${ws}/settings/integrations`,
  settingsSupportTeams: (ws: string) => `/w/${ws}/settings/support-teams`,
  settingsSupportDesk: (ws: string) => `/w/${ws}/settings/support-desk`,
  settingsAudit: (ws: string) => `/w/${ws}/settings/audit`,
  settingsNotifications: (ws: string) => `/w/${ws}/settings/notifications`,
  settingsClientPreview: (ws: string) => `/w/${ws}/settings/view-as-client`,
  settingsMemberPreview: (ws: string) => `/w/${ws}/settings/view-as-member`,

  /** Owner/Admin-only, read-only rendering of the client-facing portal. */
  clientPreviewRequests: (clientId: string, returnTo?: string) => previewPath(clientId, 'requests', returnTo),
  clientPreviewItems: (clientId: string, returnTo?: string) => previewPath(clientId, 'items', returnTo),
  clientPreviewItem: (clientId: string, itemId: string, returnTo?: string) =>
    previewPath(clientId, `items/${encodeURIComponent(itemId)}`, returnTo),

  /**
   * The client portal — a SEPARATE surface, deliberately outside `/w/:workspaceSlug`.
   *
   * An external caller has no workspace and no ability payload, so there is nothing for a
   * workspace segment to name. Keeping these paths flat is what makes "is this an internal
   * URL or a customer-facing one?" answerable by looking at it.
   */
  portal: () => '/portal',
  portalRequests: () => '/portal/requests',
  portalItems: () => '/portal/items',
  portalItem: (itemId: string) => `/portal/items/${itemId}`,

  kitchenSink: () => '/kitchen-sink',
} as const
